import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { protectedResources } from "../authConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

// ─── Module-level shared state (all hook instances share this) ───
let _cachedToken: { token: string; expiresOn: number; accountId: string } | null = null;
let _pendingRefresh: { accountId: string; id: symbol; promise: Promise<string> } | null = null;
let _cacheGeneration = 0;
let _redirecting = false;

const TOKEN_BUFFER_MS = 60_000; // refresh 60s before expiry
const FALLBACK_TTL_MS = 300_000; // 5min if expiresOn missing
const NO_ACTIVE_ACCOUNT_MESSAGE = "No active account";

const getE2EAuthBypassToken = () => {
  if (process.env.NODE_ENV !== "development") return null;
  try {
    return window.sessionStorage.getItem("e2eAuthBypassToken") || null;
  } catch {
    return null;
  }
};

const isNoActiveAccountError = (err: unknown) =>
  err instanceof Error && err.message === NO_ACTIVE_ACCOUNT_MESSAGE;

/** Clear the shared cache (useful for logout / account switch). */
export function clearTokenCache() {
  _cacheGeneration++;
  _cachedToken = null;
  _pendingRefresh = null;
  _redirecting = false;
}

const useAuthToken = (): {
  token: string | null;
  error: string | null;
  getToken: () => Promise<string>;
} => {
  const { instance, accounts, inProgress } = useMsal();
  const e2eAuthBypassToken = getE2EAuthBypassToken();
  const [token, setToken] = useState<string | null>(() => e2eAuthBypassToken);
  const [error, setError] = useState<string | null>(null);

  // Called before every API request — returns a cached or fresh token
  const getToken = useCallback(async (): Promise<string> => {
    if (e2eAuthBypassToken) return e2eAuthBypassToken;

    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (!account) throw new Error(NO_ACTIVE_ACCOUNT_MESSAGE);

    const accountId = account.homeAccountId || account.username || "";

    // Cache hit: return immediately if token is fresh and for the same account
    if (
      _cachedToken &&
      _cachedToken.accountId === accountId &&
      _cachedToken.expiresOn - Date.now() > TOKEN_BUFFER_MS
    ) {
      return _cachedToken.token;
    }

    // Deduplicate: if a refresh is already in-flight, piggyback on it
    if (_pendingRefresh?.accountId === accountId) {
      return _pendingRefresh.promise;
    }

    const generation = _cacheGeneration;
    const refreshId = Symbol();
    let refreshFinished = false;
    const assertAccountCurrent = () => {
      const current = instance.getActiveAccount() || instance.getAllAccounts()[0];
      const currentId = current?.homeAccountId || current?.username || "";
      if (generation !== _cacheGeneration || currentId !== accountId) {
        throw new DOMException("Authenticated identity changed during token acquisition.", "AbortError");
      }
    };
    const refreshPromise = (async () => {
      try {
        const resp = await instance.acquireTokenSilent({
          scopes: protectedResources.baseURL.scopes.access,
          account,
          forceRefresh: false,
        });

        assertAccountCurrent();
        _cachedToken = {
          token: resp.accessToken,
          expiresOn: resp.expiresOn?.getTime() ?? (Date.now() + FALLBACK_TTL_MS),
          accountId,
        };

        return resp.accessToken;
      } catch (e: any) {
        assertAccountCurrent();
        // Clear stale cache on failure
        _cachedToken = null;

        if (e instanceof InteractionRequiredAuthError) {
          if (_redirecting) {
            throw new Error("Redirecting to login…");
          }
          _redirecting = true;
          try {
            await instance.acquireTokenRedirect({
              scopes: protectedResources.baseURL.scopes.access,
              account,
            });
          } catch (redirectError) {
            assertAccountCurrent();
            _redirecting = false;
            throw redirectError;
          }
          // acquireTokenRedirect navigates away; this line is never reached
          throw new Error("Redirecting to login…");
        }
        throw e;
      } finally {
        refreshFinished = true;
        if (_pendingRefresh?.id === refreshId) _pendingRefresh = null;
      }
    })();

    // SDK preflight can throw before the async refresh yields for registration.
    if (!refreshFinished) _pendingRefresh = { accountId, id: refreshId, promise: refreshPromise };
    return refreshPromise;
  }, [instance, e2eAuthBypassToken]);

  // Initial token acquisition (sets state for guards like `if (!token) return null`)
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (e2eAuthBypassToken) {
        setToken(e2eAuthBypassToken);
        setError(null);
        return;
      }

      if (inProgress !== "none") return;

      try {
        const accessToken = await getToken();
        if (!cancelled) {
          setToken(accessToken);
          setError(null);
        }
      } catch (e: any) {
        if (!cancelled) {
          if (e instanceof DOMException && e.name === "AbortError") return;
          if (!isNoActiveAccountError(e)) {
            console.error("❌ Token acquisition error:", e);
          }
          setToken(null);
          setError(isNoActiveAccountError(e) ? null : e?.message || "TOKEN_ERROR");
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [inProgress, getToken, e2eAuthBypassToken, accounts]);

  return { token, error, getToken };
};

export default useAuthToken;